/* eslint-disable @typescript-eslint/no-unused-vars */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  GetAllWarehouseApiResponse,
  Warehouse,
} from "../../../@types/DRO/GetAllWarehouseResponse";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import { CreateTransferOrderBody } from "../../../@types/DTO/CreateTransferOrderBody";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { DownloadPdfApi } from "../../../api/PdfDownload/DownloadPdfApi";
import { TransferOrderApi } from "../../../api/TransferOrder/TransferOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";
import VendorAllocation, {
  VendorOption,
  VendorSplitEntry,
} from "../../Layouts/VendorAllocation/VendorAllocation";

type VendorSplitErrors = {
  rows: { [splitId: number]: string };
  section?: string;
};

const VENDOR_ROW_ERROR = "Please select Vendor and fill the quantities";

const getVendorSplitErrors = (
  mainQuantity: number,
  splits: VendorSplitEntry[]
): VendorSplitErrors | null => {
  const rows: VendorSplitErrors["rows"] = {};

  splits.forEach((split) => {
    const qty = Number(split.quantity) || 0;
    const noVendor =
      split.vendorId === "" || split.vendorId === null || split.vendorId === undefined;
    if (noVendor || qty <= 0) {
      rows[split.id] = VENDOR_ROW_ERROR;
    }
  });

  return Object.keys(rows).length > 0 ? { rows } : null;
};

const CreateTransferOrder: React.FC = () => {
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const { id: orderId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const [items, setItems] = useState<TransferOrderPartDetails[]>([
    {
      description: "",
      referenceCode: "",
      quantity: 0,
      // unit: "Units",
      price: 0,
      tax: 0,
      // total: 0,
      id: 0, // Changed from -1 to 0
      externalId: 0,
      type: "",
      status: 0,
      costPrice: 0,
      averageCostPrice: 0,
      salesPrice: 0,
      penaltyType: "",
      deleted: false,
      updatedBy: 0,
      vendorAllocations: [], // Initialize as an empty array
    },
  ]);

  const [newItem, setNewItem] = useState({
    description: "",
    referenceCode: "",
    quantity: "",
    unit: "Units",
    price: "",
    taxes: "0%",
    total: "",
    partId: "",
  });

  const [warehouses, setWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [destinationWarehouses, setDestinationWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [sourceWarehouse, setSourceWarehouse] = useState<Warehouse | null>(
    null
  );
  const [destinationWarehouse, setDestinationWarehouse] =
    useState<Warehouse | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const [destinationPageNumber, setDestinationPageNumber] = useState(0);
  const [hasMoreWarehouses, setHasMoreWarehouses] = useState(true);
  const [hasMoreDestinationWarehouses, setHasMoreDestinationWarehouses] =
    useState(true);
  const [isLoadingWarehouses, setIsLoadingWarehouses] = useState(false);
  const [isLoadingDestinationWarehouses, setIsLoadingDestinationWarehouses] =
    useState(false);
  const rowsPerPage = 10;

  // Add refs for debouncing
  const sourceDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const destinationDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [isDownloading, setIsDownloading] = useState(false);

  // ---- Vendor state ----
  const [vendorAllocationOptions, setVendorAllocationOptions] = useState<VendorOption[]>([]);
  const [isLoadingVendorOptions, setIsLoadingVendorOptions] = useState(false);
  const [splitActiveRows, setSplitActiveRows] = useState<{
    [key: number]: boolean;
  }>({});
  const [vendorSplits, setVendorSplits] = useState<{
    [key: number]: VendorSplitEntry[];
  }>({});

  const fetchVendorOptionsForWarehouse = useCallback(async (warehouseId: number) => {
    try {
      setIsLoadingVendorOptions(true);
      const response = await AllPartApi.api.getPartList(
        0,
        10,
        "",
        undefined,
        undefined,
        undefined,
        warehouseId
      );

      const parts = response?.data?.data || [];
      const vendorMap = new Map<string, VendorOption>();
      parts.forEach((part) => {
        (part.vendorAllocations || []).forEach((allocation: any) => {
          if (allocation?.vendorId == null) return;
          const key = `${part.id}-${allocation.vendorId}`;
          if (!vendorMap.has(key)) {
            vendorMap.set(key, {
              productId: part.id,
              vendorId: allocation.vendorId,
              vendorName: allocation.vendorName,
              quantity: allocation.quantity || 0,
            });
          }
        });
      });
      setVendorAllocationOptions(Array.from(vendorMap.values()));
      
    } catch (error) {
      console.error("Error fetching vendor options", error);
      setVendorAllocationOptions([]);
    } finally {
      setIsLoadingVendorOptions(false);
    }
  }, []);

  useEffect(() => {
    // alert(JSON.stringify(sourceWarehouse));
    if (sourceWarehouse) {
      fetchVendorOptionsForWarehouse(sourceWarehouse.id);
    } else {
      setVendorAllocationOptions([]);
    }
  }, [sourceWarehouse, fetchVendorOptionsForWarehouse]);

  const [vendorSplitErrors, setVendorSplitErrors] = useState<{
    [itemIndex: number]: VendorSplitErrors;
  }>({});

  const validateVendorSplits = (): boolean => {
    const errors: typeof vendorSplitErrors = {};
    items.forEach((item, index) => {
      if (!splitActiveRows[index]) return; // only items with vendor allocation enabled
      const result = getVendorSplitErrors(item.quantity, vendorSplits[index] || []);
      if (result) errors[index] = result;
    });
    setVendorSplitErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Clears errors live as the user fixes them (only re-checks rows that currently show an error)
  useEffect(() => {
    setVendorSplitErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next: typeof prev = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (!splitActiveRows[i] || !items[i]) return;
        const result = getVendorSplitErrors(items[i].quantity, vendorSplits[i] || []);
        if (result) next[i] = result;
      });
      return next;
    });
  }, [vendorSplits, items, splitActiveRows]);

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  const partsListRef = useRef<TransferOrderPartDetails[]>([]);

  const [filteredParts, setFilteredParts] = useState<
    TransferOrderPartDetails[]
  >([]);
  const [transitWarnings, setTransitWarnings] = useState<{
    [key: number]: { message: string; quantity: number };
  }>({});

  const handleDownloadPdf = async () => {
    if (!orderId) {
      setSnackbarMessage("Transfer Order ID is required to download PDF");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsDownloading(true);
      await DownloadPdfApi.api.getPdfLinkByTOId(Number(orderId));
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setSnackbarMessage("Failed to download PDF. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDownloading(false);
    }
  };

  // Fixed scroll handler for source warehouses
  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget;
      const threshold = 5; // Load more when 5px from bottom

      if (
        listboxNode.scrollTop + listboxNode.clientHeight >=
          listboxNode.scrollHeight - threshold &&
        hasMoreWarehouses &&
        !isLoadingWarehouses
      ) {
        setCurrentPageNumber((prevPage) => prevPage + 1);
      }
    },
    [hasMoreWarehouses, isLoadingWarehouses]
  );

  // Fixed scroll handler for destination warehouses
  const handleDestinationScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget;
      const threshold = 5; // Load more when 5px from bottom

      if (
        listboxNode.scrollTop + listboxNode.clientHeight >=
          listboxNode.scrollHeight - threshold &&
        hasMoreDestinationWarehouses &&
        !isLoadingDestinationWarehouses
      ) {
        setDestinationPageNumber((prevPage) => prevPage + 1);
      }
    },
    [hasMoreDestinationWarehouses, isLoadingDestinationWarehouses]
  );

  const fetchWarehouses = useCallback(
    async (pageNumber: number, searchQuery: string, append: boolean = true) => {
      if (isLoadingWarehouses) return;

      try {
        setIsLoadingWarehouses(true);
        const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
          pageNumber,
          rowsPerPage,
          searchQuery,
          undefined
          // true
        );

        const newWarehouses = response.data.data;
        const hasMore = pageNumber < response.data.totalPages - 1;

        if (append) {
          setWarehouses((prev) => {
            const existingIds = new Set(prev.map((w) => w.id));
            const uniqueNewWarehouses = newWarehouses.filter(
              (w) => !existingIds.has(w.id)
            );
            return [...prev, ...uniqueNewWarehouses];
          });
        } else {
          // Replace warehouses when it's a new search
          setWarehouses(newWarehouses);
        }

        setHasMoreWarehouses(hasMore);
      } catch (error) {
        console.error("Error fetching warehouses", error);
      } finally {
        setIsLoadingWarehouses(false);
      }
    },
    [isLoadingWarehouses]
  );

  const fetchDestinationWarehouses = useCallback(
    async (pageNumber: number, searchQuery: string, append: boolean = true) => {
      if (isLoadingDestinationWarehouses) return;

      try {
        setIsLoadingDestinationWarehouses(true);
        const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
          pageNumber,
          rowsPerPage,
          searchQuery
          // true // myWarehouseOnly parameter set to true
        );

        const newWarehouses = response.data.data;
        const hasMore = pageNumber < response.data.totalPages - 1;

        if (append) {
          setDestinationWarehouses((prev) => {
            const existingIds = new Set(prev.map((w) => w.id));
            const uniqueNewWarehouses = newWarehouses.filter(
              (w) => !existingIds.has(w.id)
            );
            return [...prev, ...uniqueNewWarehouses];
          });
        } else {
          // Replace warehouses when it's a new search
          setDestinationWarehouses(newWarehouses);
        }

        setHasMoreDestinationWarehouses(hasMore);
      } catch (error) {
        console.error("Error fetching destination warehouses", error);
      } finally {
        setIsLoadingDestinationWarehouses(false);
      }
    },
    [isLoadingDestinationWarehouses]
  );

  // Handle source warehouse search input change with debouncing
  const handleSourceInputChange = useCallback(
    (event: any, value: string, reason: string) => {
      // Only handle user input, not programmatic changes
      if (reason !== "input") return;

      // Clear existing timeout
      if (sourceDebounceRef.current) {
        clearTimeout(sourceDebounceRef.current);
      }

      // Set new timeout
      sourceDebounceRef.current = setTimeout(() => {
        setCurrentPageNumber(0);
        setHasMoreWarehouses(true);
        fetchWarehouses(0, value, false);
      }, 300); // 300ms debounce delay
    },
    [fetchWarehouses]
  );

  // Handle destination warehouse search input change with debouncing
  const handleDestinationInputChange = useCallback(
    (event: any, value: string, reason: string) => {
      // Only handle user input, not programmatic changes
      if (reason !== "input") return;

      // Clear existing timeout
      if (destinationDebounceRef.current) {
        clearTimeout(destinationDebounceRef.current);
      }

      // Set new timeout
      destinationDebounceRef.current = setTimeout(() => {
        setDestinationPageNumber(0);
        setHasMoreDestinationWarehouses(true);
        fetchDestinationWarehouses(0, value, false);
      }, 300); // 300ms debounce delay
    },
    [fetchDestinationWarehouses]
  );

  function fetchPaginatedPartsBySearchTerm(
    currentPageNumber: number,
    currentSearchQueryTerm: string,
    warehouseId?: number
  ): Promise<{
    options: OptionType<TransferOrderPartDetails>[];
    hasMore: boolean;
  }> {
    return AllPartApi.api
      .getPartList(
        currentPageNumber,
        rowsPerPage,
        currentSearchQueryTerm,
        undefined,
        undefined,
        undefined,
        warehouseId
      )
      .then(({ data: response }) => ({
        options: response.data.map((part) => ({
          value: part.id.toString(),
          label: part.description,
          data: part,
        })),
        hasMore: currentPageNumber < response.totalPages - 1,
      }))
      .catch(() => ({ options: [], hasMore: false }));
  }

  const handlePartSelect =
    (index: number) =>
    async (event: any, value: OptionType<TransferOrderPartDetails> | null) => {
      if (value) {
        if (value.data) {
          const partDetails = value.data;
          const newItems = [...items];
          newItems[index] = {
            ...newItems[index],
            description: partDetails.description || "",
            referenceCode: partDetails.referenceCode || "",
            id: partDetails.id,
            price: +(partDetails.costPrice?.toString() || "0"),
            tax: partDetails.tax || 0,
            quantity: 0,
            vendorAllocations: partDetails.vendorAllocations || [], // Preserve vendor allocations
          };
          
          setItems(newItems);

          // Hide the Vendor Allocation section and clear its values for this row
          // since the selected product changed
          setSplitActiveRows((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          setVendorSplits((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });

          // Check part transfer transition
          if (sourceWarehouse && destinationWarehouse && partDetails.id) {
            try {
              const response =
                await TransferOrderApi.api.checkPartTransferTransition({
                  sourceWarehouseId: sourceWarehouse.id,
                  destinationWarehouseId: destinationWarehouse.id,
                  partId: partDetails.id,
                }); 

              if (response.data.quantityInTransit > 0) {
                setTransitWarnings((prev) => ({
                  ...prev,
                  [index]: {
                    message: response.data.message,
                    quantity: response.data.quantityInTransit,
                  },
                }));
              } else {
                // Remove warning if quantity is 0
                setTransitWarnings((prev) => {
                  const newWarnings = { ...prev };
                  delete newWarnings[index];
                  return newWarnings;
                });
              }
            } catch (error) {
              console.error("Error checking part transfer transition:", error);
            }
          }
        }
      } else {
        // Reset the item when a part is deselected
        const newItems = [...items];
        newItems[index] = {
          description: "",
          referenceCode: "",
          costPrice: 0,
          averageCostPrice: 0,
          deleted: false,
          externalId: 0,
          id: 0,
          price: 0,
          quantity: 0,
          salesPrice: 0,
          penaltyType: "",
          status: 0,
          tax: 0,
          type: "",
          updatedBy: 0,
        };

        setItems(newItems);

        setTransitWarnings((prev) => {
          const newWarnings = { ...prev };
          delete newWarnings[index];
          return newWarnings;
        });

        // Hide the Vendor Allocation section and clear its values for this row
        // since the product was deselected
        setSplitActiveRows((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
        setVendorSplits((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      }
    };

  useEffect(() => {
    // Initial fetch without search query
    fetchWarehouses(0, "", false);
  }, []);

  useEffect(() => {
    if (currentPageNumber > 0) {
      fetchWarehouses(currentPageNumber, sourceWarehouse?.name || "", true);
    }
  }, [currentPageNumber]);

  useEffect(() => {
    if (sourceWarehouse) {
      // Reset destination state and fetch fresh data
      setDestinationWarehouses([]);
      setDestinationPageNumber(0);
      setHasMoreDestinationWarehouses(true);
      fetchDestinationWarehouses(0, "", false);
    }
  }, [sourceWarehouse]);

  useEffect(() => {
    if (sourceWarehouse && destinationPageNumber > 0) {
      fetchDestinationWarehouses(
        destinationPageNumber,
        destinationWarehouse?.name || "",
        true
      );
    }
  }, [destinationPageNumber]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (sourceDebounceRef.current) {
        clearTimeout(sourceDebounceRef.current);
      }
      if (destinationDebounceRef.current) {
        clearTimeout(destinationDebounceRef.current);
      }
    };
  }, []);

  const handleCreateTransferOrder = async (status: string) => {
    setLoading(true);
    if (!sourceWarehouse || !destinationWarehouse) {
      setSnackbarMessage("Please select source and destination warehouses");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    if (!validateVendorSplits()) {
      setLoading(false);
      return;
    }

    try {
      const orderBody: CreateTransferOrderBody = {
        sourceWarehouse: sourceWarehouse?.id,
        destinationWarehouse: destinationWarehouse.id,
        status,
        partsList: items
          .filter((item) => item.id > 0) // Only include items with valid IDs
          .map((item) => ({
            partId: item.id || 0,
            quantityOrdered: item.quantity,
            vendorAllocations: vendorSplits[items.indexOf(item)]?.map((split) => ({
              vendorId: split.vendorId,
              quantity: split.quantity,
            }))
          })),
      };

      const response = await TransferOrderApi.api.createTransferOrder(
        orderBody
      );

      const successMessage =
        response?.message || "Transfer order created successfully";
      setSnackbarMessage(successMessage);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setTimeout(() => {
        navigate(`/transfer-order${statusFilter ? `?status=${statusFilter}` : ""}`);
      }, 1000);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Error creating transfer order";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, newItem as unknown as TransferOrderPartDetails]);
    setNewItem({
      description: "",
      referenceCode: "",
      quantity: "",
      unit: "Units",
      price: "",
      taxes: "0%",
      total: "",
      partId: "",
    });
  };

  const handleDeleteItem = (index: number) => {
    setVendorSplitErrors({});
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);

    // Remove any transit warnings for deleted items
    setTransitWarnings((prev) => {
      const newWarnings = { ...prev };
      // Adjust indices for warnings after the deleted item
      const adjustedWarnings: typeof newWarnings = {};
      Object.entries(newWarnings).forEach(([key, value]) => {
        const warningIndex = parseInt(key);
        if (warningIndex < index) {
          adjustedWarnings[warningIndex] = value;
        } else if (warningIndex > index) {
          adjustedWarnings[warningIndex - 1] = value;
        }
      });
      return adjustedWarnings;
    });

    // Adjust split state indices for the deleted item
    setSplitActiveRows((prev) => {
      const updated: typeof prev = {};
      Object.entries(prev).forEach(([key, value]) => {
        const rowIndex = parseInt(key);
        if (rowIndex < index) {
          updated[rowIndex] = value;
        } else if (rowIndex > index) {
          updated[rowIndex - 1] = value;
        }
      });
      return updated;
    });

    setVendorSplits((prev) => {
      const updated: typeof prev = {};
      Object.entries(prev).forEach(([key, value]) => {
        const rowIndex = parseInt(key);
        if (rowIndex < index) {
          updated[rowIndex] = value;
        } else if (rowIndex > index) {
          updated[rowIndex - 1] = value;
        }
      });
      return updated;
    });
  };

  // ---- Vendor split handlers ----
  const handleToggleSplitVendor = (index: number) => {
    const isCurrentlyActive = !!splitActiveRows[index];

    if (isCurrentlyActive) {
      // Second click - close/hide the split section
      setSplitActiveRows((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      setVendorSplits((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      return;
    }

    // First click - open the split section with a minimum of 2 vendor rows
    setSplitActiveRows((prev) => ({ ...prev, [index]: true }));
    setVendorSplits((prev) => {
      if (prev[index] && prev[index].length >= 1) return prev;
      return {
        ...prev,
        [index]: [
          {
            id: Date.now(),
            vendorId: "",
            vendorName: "",
            quantity: items[index]?.quantity || 0,
            unitPrice: items[index]?.price || 0,
          }
        ],
      };
    });
  };

  const handleAddVendorSplitRow = (index: number) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      return {
        ...prev,
        [index]: [
          ...existing,
          {
            id: Date.now() + Math.random(),
            vendorId: "",
            vendorName: "",
            quantity: 0,
            unitPrice: items[index]?.price || 0,
          },
        ],
      };
    });
  };

  const handleRemoveVendorSplitRow = (index: number, splitId: number) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      // Keep a minimum of 1 vendor row in the split section
      if (existing.length <= 1) return prev;
      const updated = existing.filter((split) => split.id !== splitId);
      return { ...prev, [index]: updated };
    });
  };

  const handleVendorSplitFieldChange = (
    index: number,
    splitId: number,
    field: "vendorId" | "vendorName" | "quantity",
    value: string | number
  ) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      const updated = existing.map((split) =>
        split.id === splitId ? { ...split, [field]: value } : split
      );
      return { ...prev, [index]: updated };
    });
  };

  const calculateTotals = () => {
    let totalAmount = 0;
    let totalGST = 0;

    items.forEach((item) => {
      const quantity = item.quantity || 0;
      const price = item.price || 0;
      const taxRate = item.tax / 100 || 0;

      const itemTotal = quantity * price;
      const itemGST = itemTotal * taxRate;

      totalAmount += itemTotal;
      totalGST += itemGST;
    });

    return {
      totalAmount: totalAmount.toFixed(2),
      totalGST: totalGST.toFixed(2),
      overallTotal: (totalAmount + totalGST).toFixed(2),
    };
  };

  const vendorOptionsByItem = useMemo(() => {
    const result: { [index: number]: VendorOption[] } = {};
    items.forEach((item, index) => {
      if (!item.id || item.id <= 0) {
        result[index] = [];
        return;
      }

      const own: VendorOption[] = ((item.vendorAllocations as any[]) || [])
        .filter((a) => a?.vendorId != null)
        .map((a) => ({
          productId: item.id,
          vendorId: a.vendorId,
          vendorName: a.vendorName,
          quantity: a.quantity || 0,
        }));

      result[index] =
        own.length > 0
          ? own
          : vendorAllocationOptions.filter((o) => o.productId === item.id);
    });
    return result;
  }, [items, vendorAllocationOptions]);

  const { totalAmount, totalGST, overallTotal } = calculateTotals();

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-full p-4">
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "600",
                color: "#1E1E1E",
                textAlign: "left",
                fontSize: "1.5rem",
                mb: 0,
              }}>
              Create Transfer Order
            </Typography>

            {orderId && (
              <Button
                variant="contained"
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                sx={{
                  backgroundColor: "#1366D9",
                  color: "white",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  "&:hover": {
                    backgroundColor: "#0f5cc0",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                  },
                  "&:disabled": {
                    backgroundColor: "#B0C4DE",
                  },
                }}>
                {isDownloading ? "Downloading..." : "Download PDF"}
              </Button>
            )}
          </Box>

          <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
            <Link
              to="/home"
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
              Dashboard
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <Link
              to={`/transfer-order${statusFilter ? `?status=${statusFilter}` : ""}`}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
              Orders
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">
              {orderId ? "Edit" : "Create"} Order
            </span>
          </div>
        </Box>
        <Paper
          sx={{
            p: 4,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            maxWidth: "1600px",
            margin: "0 auto",
          }}>
          {/* Form Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              {/* Source Location */}
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Source Location
                  </label>
                  <Autocomplete
                    fullWidth
                    options={warehouses}
                    getOptionLabel={(option) => option.name}
                    value={sourceWarehouse}
                    loading={isLoadingWarehouses}
                    onInputChange={handleSourceInputChange}
                    onChange={(_, newValue) => {
                      setSourceWarehouse(newValue);
                      setDestinationWarehouse(null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                          endAdornment: (
                            <>
                              {isLoadingWarehouses ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: handleScroll,
                      style: { maxHeight: "200px" },
                    }}
                  />
                </Box>
              </Grid>

              {/* Deliver To */}
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Deliver To
                  </label>
                  <Autocomplete
                    fullWidth
                    disabled={!sourceWarehouse}
                    options={destinationWarehouses.filter(
                      (wareHouse) => wareHouse.id !== sourceWarehouse?.id
                    )}
                    getOptionLabel={(option) => option.name}
                    value={destinationWarehouse}
                    loading={isLoadingDestinationWarehouses}
                    onInputChange={handleDestinationInputChange}
                    onChange={(_, newValue) => {
                      setDestinationWarehouse(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                          endAdornment: (
                            <>
                              {isLoadingDestinationWarehouses ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: handleDestinationScroll,
                      style: { maxHeight: "200px" },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Items Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "left",
                fontWeight: "600",
                color: "#1E1E1E",
                mb: 2,
                fontSize: "1.125rem",
              }}>
              Items
            </Typography>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 1400 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        width: "23%",
                      }}>
                      Product Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        width: "150px",
                      }}>
                      Reference Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Unit Of Measure
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Unit Price
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Tax
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Total
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell sx={{ fontSize: "0.875rem" }}>
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ position: "relative" }}> 
                            <AppAutoCompleteWithSearchAPI
                              onChange={handlePartSelect(index)}
                              initialPageNumber={0}
                              disabled={!sourceWarehouse || !destinationWarehouse}
                              value={
                                item.id && item.id > 0
                                  ? {
                                      value: item.id.toString(),
                                      label:
                                        item.referenceCode &&
                                        item.referenceCode !== ""
                                          ? `${item.description} (${item.referenceCode})`
                                          : item.description,
                                      data: item,
                                    }
                                  : null
                              }
                              refetchKey={sourceWarehouse?.id}             
                              getData={({ currentPageNumber, currentSearchQueryTerm }) =>
                                fetchPaginatedPartsBySearchTerm(
                                  currentPageNumber,
                                  currentSearchQueryTerm,
                                  sourceWarehouse?.id
                                )
                              }
                              getOptionLabel={(
                                option: OptionType<TransferOrderPartDetails>
                              ) =>
                                option.data?.referenceCode &&
                                option.data?.referenceCode !== ""
                                  ? `${option.data.description} (${option.data.referenceCode})`
                                  : option.data?.description || ""
                              }
                              filterOptions={(options) => options}
                            /> 
                            {transitWarnings[index] && (
                              <Box sx={{ mt: 1 }}>
                                {/* <Chip
                                  icon={<InfoIcon />}
                                  label={`${transitWarnings[index].quantity} units in transit`}
                                  color="warning"
                                  size="small"
                                  sx={{ fontSize: "0.75rem" }}
                                /> */}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: "warning.main",
                                    mt: 0.5,
                                    fontSize: "0.7rem",
                                  }}>
                                  {transitWarnings[index].message}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={item.referenceCode}
                            variant="standard"
                            disabled
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TextField
                              value={item.quantity || ""}
                              onChange={(e) => {
                                const newItems = [...items];
                                const inputValue = e.target.value;

                                if (inputValue === "0") {
                                  return;
                                }

                                newItems[index].quantity = +inputValue;
                                const quantity = parseInt(inputValue, 10) || 0;
                                const price = newItems[index].price || 0;
                                const taxRate = newItems[index].tax / 100 || 0;
                                // const total = quantity * price * (1 + taxRate);
                                // newItems[index].total = total.toFixed(2);
                                setItems(newItems);
                              }}
                              onBlur={() => {
                                const newItems = [...items];
                                const quantity = newItems[index].quantity;
                                if (isNaN(quantity) || quantity < 1) {
                                  newItems[index].quantity = 0;
                                  const price = newItems[index].price || 0;
                                  const taxRate = newItems[index].tax / 100 || 0;
                                  // const total = 1 * price * (1 + taxRate);
                                  // newItems[index].total = total.toFixed(2);
                                }
                                setItems(newItems);
                              }}
                              variant="standard"
                              disabled={!item.description}
                              InputProps={{
                                style: { fontSize: "0.875rem" },
                              }}
                            /> 

                            <Button
                              variant="outlined"
                              size="small"
                              disabled={!item.description || !item.quantity}
                              onClick={() => handleToggleSplitVendor(index)}
                              sx={{
                                width: "120px",
                                flex: "none",
                                textTransform: "none",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                borderColor: splitActiveRows[index] ? "#1366D9" : "#D0D5DD",
                                color: splitActiveRows[index] ? "#1366D9" : "#344054",
                                backgroundColor: splitActiveRows[index] ? "#EEF4FE" : "transparent",
                                whiteSpace: "nowrap",
                                "&:hover": {
                                  borderColor: "#1366D9",
                                  backgroundColor: splitActiveRows[index] ? "#EEF4FE" : "transparent",
                                },
                              }}>
                              {splitActiveRows[index] ? "Selecting Vendor" : "Select Vendor"}
                            </Button>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={"Units"}
                            variant="standard"
                            disabled
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={item.price || ""}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index].price = +e.target.value;
                              const quantity = newItems[index].quantity || 0;
                              const price = parseFloat(e.target.value) || 0;
                              const taxRate = newItems[index].tax / 100 || 0;
                              const total = quantity * price * (1 + taxRate);
                              // newItems[index].total = total.toFixed(2);
                              setItems(newItems);
                            }}
                            variant="standard"
                            disabled
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  ₹
                                </InputAdornment>
                              ),
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={item.tax || ""}
                            variant="standard"
                            disabled
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={(
                              item.quantity *
                              item.price *
                              (1 + item.tax / 100)
                            ).toFixed(2)}
                            variant="standard"
                            disabled
                            InputProps={{
                              style: { fontSize: "0.875rem" },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <IconButton
                              onClick={() => handleDeleteItem(index)}
                              sx={{ color: "#FF0000" }}
                              size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {splitActiveRows[index] && (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            sx={{ backgroundColor: "#FFFFFF", padding: 1, border: "none" }}>
                            <VendorAllocation
                              index={index}
                              productId={item.id}
                              quantity={item.quantity}
                              price={item.price}
                              tax={item.tax}
                              vendorSplits={vendorSplits[index] || []}
                              vendorAllocationOptions={vendorOptionsByItem[index] || []}   // was: vendorAllocationOptions
                              isLoadingVendorOptions={isLoadingVendorOptions}
                              onAddVendorSplitRow={handleAddVendorSplitRow}
                              onRemoveVendorSplitRow={handleRemoveVendorSplitRow}
                              onVendorSplitFieldChange={handleVendorSplitFieldChange}
                              onCancel={handleToggleSplitVendor}
                            />
                            {vendorSplitErrors[index] && (
                              <Box sx={{pb: 1 , mt:0.5}}>
                                {Object.entries(vendorSplitErrors[index].rows).map(([splitId, message]) => {
                                  const rowNumber =
                                    (vendorSplits[index] || []).findIndex((s) => String(s.id) === splitId) + 1;
                                  return (
                                    <Typography
                                      key={splitId}
                                      variant="caption"
                                      sx={{ display: "block", color: "error.main", fontSize: "0.9rem", fontWeight: 'bold' }}>
                                      Vendor {rowNumber}: {message}
                                    </Typography>
                                  );
                                })}
                                {vendorSplitErrors[index].section && (
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", color: "error.main", fontSize: "0.9rem", fontWeight: 'bold'  }}>
                                    {vendorSplitErrors[index].section}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <IconButton
                onClick={handleAddItem}
                disabled={!sourceWarehouse || !destinationWarehouse}
                sx={{
                  color:
                    !sourceWarehouse || !destinationWarehouse
                      ? "#ccc"
                      : "#1976D2",
                  mr: 1,
                }}
                size="small">
                <AddIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body1"
                sx={{
                  cursor:
                    !sourceWarehouse || !destinationWarehouse
                      ? "not-allowed"
                      : "pointer",
                  color:
                    !sourceWarehouse || !destinationWarehouse
                      ? "#ccc"
                      : "#1976D2",
                  fontSize: "0.875rem",
                }}
                onClick={
                  !sourceWarehouse || !destinationWarehouse
                    ? undefined
                    : handleAddItem
                }>
                Add Item
              </Typography>
              {(!sourceWarehouse || !destinationWarehouse) && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 2,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}>
                  (Select source and destination warehouses first)
                </Typography>
              )}
            </Box>
          </Box>

          {/* Totals and Actions Section */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ mb: 2, textAlign: "right" }}>
              <Typography
                variant="body1"
                sx={{ color: "#646464", fontSize: "0.875rem" }}>
                Amount - ₹ {totalAmount}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#646464", fontSize: "0.875rem" }}>
                IGST - ₹ {totalGST}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#1E1E1E",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                }}>
                Total - ₹ {overallTotal}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => navigate(`/transfer-order${statusFilter ? `?status=${statusFilter}` : ""}`)}
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  backgroundColor: "#F94932",
                }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleCreateTransferOrder("DRAFT")}
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  backgroundColor: "#1366D9",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  "&:hover": { backgroundColor: "#1366D9" },
                }}>
                Draft
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleCreateTransferOrder("CREATED")}
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  backgroundColor: "#1E1E1E",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  "&:hover": { backgroundColor: "#000000" },
                }}>
                Create
              </Button>
            </Box>
          </Box>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={2000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Paper>
      </div>
    </div>
  );
};

export default CreateTransferOrder;
